import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, finalize, shareReplay } from 'rxjs';
import { GlobalErrorService } from '@services/global-error.service';
import { environment } from '@environments/environment';
import { ApiResponse } from '../models/iskill.models';

@Injectable({
  providedIn: 'root',
})
export class IskillApiService {
  private baseUrl = environment.iskillApiUrl;

  /**
   * In-flight cache for /bootstrap — deduplicates concurrent bootstrap calls
   * for the same view (2026-08-13 prod incident: one user fired 6 bootstraps
   * in 2.4 s during a route/subscription bounce, contributing to Hikari
   * pool exhaustion). While a request is pending, subsequent callers subscribe
   * to the same Observable and get the same response; entry clears on complete
   * OR error so a fresh call after that fires normally.
   */
  private bootstrapInFlight: { view: string; observable: Observable<ApiResponse<unknown>> } | null = null;

  constructor(
    private http: HttpClient,
    private errorHandler: GlobalErrorService,
  ) {}

  get<T>(endpoint: string, params?: Record<string, string>): Observable<ApiResponse<T>> {
    return this.http
      .get<ApiResponse<T>>(`${this.baseUrl}${endpoint}`, { params })
      .pipe(catchError((error) => this.errorHandler.logError(error)));
  }

  /** Blob GET — for file downloads (XLSX / CSV / PDF). Bypasses the ApiResponse<T> envelope. */
  getBlob(endpoint: string, params?: Record<string, string>): Observable<Blob> {
    return this.http
      .get(`${this.baseUrl}${endpoint}`, { params, responseType: 'blob' })
      .pipe(catchError((error) => this.errorHandler.logError(error)));
  }

  /**
   * Dedupes concurrent /bootstrap calls per view. Any caller subscribed while
   * a request is in flight receives the same response — no extra HTTP call.
   */
  getBootstrap<T>(view: string): Observable<ApiResponse<T>> {
    if (this.bootstrapInFlight && this.bootstrapInFlight.view === view) {
      return this.bootstrapInFlight.observable as Observable<ApiResponse<T>>;
    }
    const observable = this.http
      .get<ApiResponse<T>>(`${this.baseUrl}/bootstrap`, { params: { view } })
      .pipe(
        catchError((error) => this.errorHandler.logError(error)),
        shareReplay(1),
        finalize(() => {
          if (this.bootstrapInFlight?.view === view) this.bootstrapInFlight = null;
        }),
      );
    this.bootstrapInFlight = { view, observable: observable as Observable<ApiResponse<unknown>> };
    return observable;
  }

  post<T>(endpoint: string, body: unknown): Observable<ApiResponse<T>> {
    return this.http
      .post<ApiResponse<T>>(`${this.baseUrl}${endpoint}`, body)
      .pipe(catchError((error) => this.errorHandler.logError(error)));
  }

  put<T>(endpoint: string, body: unknown): Observable<ApiResponse<T>> {
    return this.http
      .put<ApiResponse<T>>(`${this.baseUrl}${endpoint}`, body)
      .pipe(catchError((error) => this.errorHandler.logError(error)));
  }

  patch<T>(endpoint: string, body: unknown): Observable<ApiResponse<T>> {
    return this.http
      .patch<ApiResponse<T>>(`${this.baseUrl}${endpoint}`, body)
      .pipe(catchError((error) => this.errorHandler.logError(error)));
  }

  delete<T>(endpoint: string): Observable<ApiResponse<T>> {
    return this.http
      .delete<ApiResponse<T>>(`${this.baseUrl}${endpoint}`)
      .pipe(catchError((error) => this.errorHandler.logError(error)));
  }

  getHealth(): Observable<ApiResponse<unknown>> {
    return this.get('/health');
  }

  getMe(): Observable<ApiResponse<unknown>> {
    return this.get('/me');
  }

  getPeople(scope: string = 'subtree'): Observable<ApiResponse<unknown>> {
    return this.get('/people', { scope });
  }

  /** V51 perf split: /people returns a slim T-Score (composite + labels
   *  only). Person-card fetches the full DTO — pillarDetails + category
   *  gaps — via this endpoint on person select. */
  getPersonTScore(employeeId: string): Observable<ApiResponse<unknown>> {
    return this.get(`/people/${employeeId}/tscore`);
  }

  /**
   * GET a binary file (.xlsx export, etc.) and trigger a browser download.
   * Reads the response as a Blob and honours the server's Content-Disposition
   * filename when present, falling back otherwise.
   */
  downloadBlob(endpoint: string, params: Record<string, string>, fallbackFilename: string): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.http
        .get(`${this.baseUrl}${endpoint}`, { params, responseType: 'blob', observe: 'response' })
        .subscribe({
          next: (resp) => {
            const blob = resp.body!;
            const disposition = resp.headers.get('content-disposition') || '';
            const match = /filename="?([^";]+)"?/.exec(disposition);
            const filename = match?.[1] || fallbackFilename;
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            subscriber.next();
            subscriber.complete();
          },
          error: (err) => subscriber.error(err),
        });
    });
  }

}
